import React, { useCallback, useEffect, useRef, useState } from "react";
import { DateTime } from "luxon";

import "./customDatePickerWidth.css";
import Button from "../components/Button";
import { ColorSelectorGroup } from "./ColorSelectorGroup";

const API_BASE = "https://openholidaysapi.org";
const COLUMN_COUNT = 8;

const fetchJson = async (url) => {
	const res = await fetch(url);
	if (!res.ok) {
		throw new Error(`OpenHolidays API error: ${res.status}`);
	}
	return res.json();
};

const capitalizeFirst = (s) =>
	s ? s.charAt(0).toUpperCase() + s.slice(1) : s;

const getLocalizedText = (arr, language) => {
	if (!arr || arr.length === 0) {
		return "";
	}
	const lang = language?.toUpperCase() || "EN";
	return (
		arr.find((n) => n.language === lang)?.text ??
		arr.find((n) => n.language === "EN")?.text ??
		arr[0].text ??
		""
	);
};

const ImportHolidays = ({ refreshPreview, closeModal, data, colors, colorOptions }) => {
	const currentYear = DateTime.now().year;

	const [year, setYear] = useState(String(currentYear));
	const [language, setLanguage] = useState("EN");
	const [countries, setCountries] = useState([]);
	const [country, setCountry] = useState("");

	const [groupsByCode, setGroupsByCode] = useState({});
	const [groupCategories, setGroupCategories] = useState({}); // Record<string, Array<{ language: string, text: string }>>
	const [groupCategoryKeys, setGroupCategoryKeys] = useState([]); // string[]
	const effectiveGroupCategoryKeys = groupCategoryKeys.length > 0 ? groupCategoryKeys : ["__GROUPS__"];

	const getGroupHeaderLabel = (categoryKey) => {
		if (categoryKey === "__GROUPS__") {
			return "";
		}
		return getLocalizedText(groupCategories[categoryKey], language) || categoryKey;
	};

	const [noTopLevelSubdivisions, setNoTopLevelSubdivisions] = useState(false);
	const [subdivisions, setSubdivisions] = useState([]);
	const [selectionPathCodes, setSelectionPathCodes] = useState([]);

	const [loadPublic, setLoadPublic] = useState(true);
	const [loadSchool, setLoadSchool] = useState(false);

	const [holidays, setHolidays] = useState([]);
	const [selected, setSelected] = useState({});
	const [selectedColors, setSelectedColors] = useState(
		colorOptions.reduce((acc, co) => {
			acc[co.id] = co.defaultColor;
			return acc;
		}, {})
	);

	const languageDebounceRef = useRef(null);
	const selectAllRef = useRef(null);

	const holidayKey = (h) =>
		[
			h.id,
			h.startDate,
			h.endDate ?? "",
		].join("|");

	useEffect(() => {
		let active = true;

		fetchJson(`${API_BASE}/Countries`)
			.then((data) => {
				if (!Array.isArray(data)) return;

				// Localize names
				const mapped = data.map((c) => ({
					isoCode: c.isoCode,
					name: getLocalizedText(c.name, language),
				}));

				// Sort alphabetically
				mapped.sort((a, b) => a.name.localeCompare(b.name));

				if (active) {
					setCountries(mapped);
				}
			})
			.catch(() => {
				if (active) {
					setCountries([]);
				}
			});

		return () => {
			active = false; // prevent late updates
		};
	}, [language]);

	useEffect(() => {
		if (!country) {
			setSubdivisions([]);
			setSelectionPathCodes([]);
			setNoTopLevelSubdivisions(false);
			return;
		}

		fetchJson(`${API_BASE}/Subdivisions?countryIsoCode=${country}`)
			.then((data) => {
				const arr = Array.isArray(data) ? data : [];
				setSubdivisions(arr);
				setNoTopLevelSubdivisions(arr.length === 0); // ← only here!
			})
			.catch(() => {
				setSubdivisions([]);
				setNoTopLevelSubdivisions(true);
			});
	}, [country]);

	const getCategoryKey = (categoryArr) => {
		if (!Array.isArray(categoryArr) || categoryArr.length === 0) {
			return "__UNKNOWN__";
		}
		return (
			categoryArr.find(c => c.language === "EN")?.text ??
			categoryArr[0].text ??
			"__UNKNOWN__"
		);
	};

	useEffect(() => {
		if (!country) {
			setGroupsByCode({});
			setGroupCategories({});
			setGroupCategoryKeys([]);
			return;
		}

		fetchJson(`${API_BASE}/Groups?countryIsoCode=${country}`)
			.then((data) => {
				if (!Array.isArray(data)) {
					setGroupsByCode({});
					setGroupCategories({});
					setGroupCategoryKeys([]);
					return;
				}

				const byCode = {};
				const categories = {};
				const keysInOrder = [];

				data.forEach((g) => {
					const categoryKey = getCategoryKey(g.category);

					// Store category localizations once (all languages)
					if (!categories[categoryKey]) {
						categories[categoryKey] = Array.isArray(g.category) ? g.category : [];
						keysInOrder.push(categoryKey);
					}

					byCode[g.code] = {
						...g,
						categoryKey,
						// This can depend on UI language (display value)
						nameText: getLocalizedText(g.name, language),
					};
				});

				setGroupsByCode(byCode);
				setGroupCategories(categories);
				setGroupCategoryKeys(keysInOrder);
			})
			.catch(() => {
				setGroupsByCode({});
				setGroupCategories({});
				setGroupCategoryKeys([]);
			});
	}, [country, language]);

	/* Language debounce reload */
	useEffect(() => {
		if (languageDebounceRef.current) {
			clearTimeout(languageDebounceRef.current);
		}

		languageDebounceRef.current = setTimeout(() => {
			setSubdivisions((prev) => [...prev]);
		}, 2000);

		return () => clearTimeout(languageDebounceRef.current);
	}, [language]);

	const loadHolidays = useCallback(async () => {
		if (!country || year.length !== 4) {
			return;
		}

		// CLEAR TABLE BEFORE RELOADING
		setHolidays([]);
		setSelected({});

		const y = parseInt(year, 10);
		if (Number.isNaN(y)) {
			return;
		}

		if (!loadPublic && !loadSchool) {
			setHolidays([]);
			setSelected({});
			return;
		}

		const validFrom = DateTime.fromObject({
			year: y,
			month: 1,
			day: 1,
		}).toISODate();

		const validTo = DateTime.fromObject({
			year: y,
			month: 12,
			day: 31,
		}).toISODate();

		const baseParams =
			`countryIsoCode=${country}` +
			`&validFrom=${validFrom}` +
			`&validTo=${validTo}` +
			`&languageIsoCode=${language || "EN"}`;

		const lastSelectedCode = selectionPathCodes.at(-1);
		const subdivisionParam = lastSelectedCode ? `&subdivisionCode=${lastSelectedCode}` : "";

		console.log("dlsklskfd " + subdivisionParam, selectionPathCodes);

		const requests = [];

		if (loadPublic) {
			requests.push(
				fetchJson(
					`${API_BASE}/PublicHolidays?${baseParams}${subdivisionParam}`
				)
			);
		}

		if (loadSchool) {
			requests.push(
				fetchJson(
					`${API_BASE}/SchoolHolidays?${baseParams}${subdivisionParam}`
				)
			);
		}

		try {
			const results = await Promise.all(requests);
			const merged = results.flat();

			merged.sort((a, b) => {
				const startA = a.startDate;
				const startB = b.startDate;

				// If start dates differ → keep previous sorting behavior
				if (startA !== startB) {
					return startA.localeCompare(startB);
				}

				// Same start date → longer holiday first
				const startDateA = DateTime.fromISO(a.startDate);
				const endDateA = a.endDate ? DateTime.fromISO(a.endDate) : startDateA;

				const startDateB = DateTime.fromISO(b.startDate);
				const endDateB = b.endDate ? DateTime.fromISO(b.endDate) : startDateB;

				const durationA = endDateA.diff(startDateA, "days").days + 1;
				const durationB = endDateB.diff(startDateB, "days").days + 1;

				return durationB - durationA; // longer first
			});

			setHolidays(merged);

			const initial = {};
			merged.forEach((h) => {
				initial[holidayKey(h)] = true;
			});
			setSelected(initial);
		} catch {
			setHolidays([]);
			setSelected({});
		}
	}, [
		country,
		year,
		language,
		selectionPathCodes,
		loadPublic,
		loadSchool,
	]);

	const toggleHoliday = (key) => {
		setSelected((prev) => ({
			...prev,
			[key]: !prev[key],
		}));
	};

	const renderSubdivisionDropdowns = () => {
		const dropdowns = [];
		let levelItems = subdivisions;

		for (let level = 0; level < COLUMN_COUNT - 4; level++) {
			if (!levelItems?.length) {
				break;
			}

			const categoryLabelRaw = getLocalizedText(levelItems[0].category, language);
			const categoryLabel = capitalizeFirst(categoryLabelRaw);

			const selectedCode = selectionPathCodes[level] ?? "";

			dropdowns.push(
				<div key={level} className="flex flex-col">
					<span className="text-xs font-semibold mb-1">
						{categoryLabel}
						{levelItems.length ? ` (${levelItems.length})` : ""}
					</span>

					<select
						className="border rounded p-2 w-full"
						value={selectedCode}
						onChange={(e) => {
							const value = e.target.value;
							console.log("value", value);

							setSelectionPathCodes((prev) => {
								const next = prev.slice(0, level);

								if (!value || value === "__ALL__") {
									return next;
								}

								next.push(value);
								return next;
							});
						}}
					>
						<option value="__ALL__">{`Load data for ${categoryLabel}`}</option>

						{levelItems.map((s) => {
							const label = getLocalizedText(s.name, language);
							const childCount = s.children?.length ?? 0;

							return (
								<option key={s.code} value={s.code}>
									{label} {childCount > 0 ? `(${childCount})` : "(0)"}
								</option>
							);
						})}
					</select>
				</div>
			);

			// Derive the selected object from *current* options (not from stored objects)
			const selectedObj = levelItems.find((x) => x.code === selectedCode);
			if (!selectedObj?.children?.length) {
				break;
			}

			levelItems = selectedObj.children;
		}

		return { dropdowns, count: dropdowns.length };
	};

	const cancelAndClose = () => {
		closeModal();
	};

	const saveAndClose = () => {
		save();
		closeModal();
	};

	const save = useCallback(() => {
		// This code is strange but I don't want to fix it now, why do we have date and startDate?

		const hols = holidays.filter(h => !!selected[holidayKey(h)]).map(h => {
			const startDate = DateTime.fromISO(h.startDate).toJSDate();
			return { id: crypto.randomUUID(), dateValid: true, date: startDate, startDate: startDate, endDate: h.endDate ? DateTime.fromISO(h.endDate).toJSDate() : startDate, colors: selectedColors /*{ background: colorBackground, border: colorBorder, text: colorText }*/, note: getLocalizedText(h.name, language) };
		});

		const newData = [...data, ...hols];
		
		refreshPreview(newData);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [/*refreshPreview causes cycle!*/ holidays, selected, selectedColors]);

	const { dropdowns, count: dropdownCount } = renderSubdivisionDropdowns();

	// A = number of 1fr columns before the TYPE column
	const A = 1 + dropdownCount;    // country + dropdowns

	// B = remaining 1fr filler columns before the LOAD button
	// Explanation: COLUMN_COUNT = total tracks
	// Tracks = 2 fixed + A (prefix) + 1 type + B (filler)
	const B = COLUMN_COUNT - (2 + A + 1); // smallest value will be 1 for the load button


	const totalCount = holidays.length;
	const selectedCount = holidays.filter(h => selected[holidayKey(h)]).length;

	const allSelected = totalCount > 0 && selectedCount === totalCount;
	const partiallySelected = selectedCount > 0 && selectedCount < totalCount;

	useEffect(() => {
		if (selectAllRef.current) {
			selectAllRef.current.indeterminate = partiallySelected;
		}
	}, [partiallySelected]);

	const toggleAll = (e) => {
		if (!e?.nativeEvent?.isTrusted) {
			return;
		}

		if (allSelected) {
			setSelected({});
		} else {
			const next = {};
			holidays.forEach(h => {
				next[holidayKey(h)] = true;
			});
			setSelected(next);
		}
	};

	return (
		<div className="fixed z-50">
			<div className="fixed inset-0 bg-gray-900 bg-opacity-60"></div>

			<div className="fixed z-50 w-11/12 max-w-7xl bg-white rounded-md px-8 py-6 space-y-4 drop-shadow-lg">
				<div className="flex justify-between items-baseline">
					<h1 className="text-2xl font-semibold">
						Import Holiday Dates
					</h1>

					<a
						href="https://www.openholidaysapi.org/en/"
						target="_blank"
						rel="noopener noreferrer"
						className="text-xs text-gray-600 hover:text-gray-800"
					>
						Data from OpenHolidays API
					</a>
				</div>

				<div
					className="grid gap-2"
					style={{
						gridTemplateColumns: `
        									    60px                                  /* Year */
        									    55px                                  /* Language */
        									    repeat(${A}, minmax(0, 1fr))          /* Country + dropdowns */
        									    max-content                           /* TYPE column */
        									    repeat(${B}, minmax(0, 1fr))          /* filler columns */
        									 `
					}}
				>
					<input
						type="text"
						value={year}
						onChange={(e) => setYear(e.target.value)}
						className="border rounded p-2 text-center"
					/>

					<input
						type="text"
						value={language}
						onChange={(e) => setLanguage(e.target.value.toUpperCase())}
						className="border rounded p-2 text-center"
						placeholder="EN"
					/>

					<div className="flex flex-col">
						<span className="text-xs font-semibold mb-1">
							Country
						</span>

						<select
							value={country}
							onChange={(e) => setCountry(e.target.value)}
							className="border rounded p-2 w-full"
						>
							<option value="">Select Country</option>

							{countries.map((c) => (
								<option key={c.isoCode} value={c.isoCode}>
									{c.name}
								</option>
							))}
						</select>
					</div>

					{noTopLevelSubdivisions ? (
						<div className="self-end text-sm text-gray-600">
							No Subdivisions found
						</div>
					) : (
						dropdowns
					)}


					<div className="flex flex-col justify-center gap-1 w-max mr-2">
						<label className="text-sm">
							<input
								type="checkbox"
								checked={loadPublic}
								onChange={(e) => setLoadPublic(e.target.checked)}
								className="mr-2"
							/>
							Load Public Holidays
						</label>
						<label className="text-sm">
							<input
								type="checkbox"
								checked={loadSchool}
								onChange={(e) => setLoadSchool(e.target.checked)}
								className="mr-2"
							/>
							Load School Holidays
						</label>
					</div>

					<Button variant="secondary" label="Load" onClick={loadHolidays } disabled={!country} style={{ gridColumn: COLUMN_COUNT }} />
				</div>

				<div
					style={{ minHeight: "200px", maxHeight: "60vh", overflowY: "auto" }}
					className="border-t border-b border-gray-300"
				>
					<table className="w-full text-sm table-fixed">
						<thead className="bg-gray-100">
							<tr>
								<th rowSpan={2} className="p-2 w-16 text-center">
									<input
										ref={selectAllRef}
										type="checkbox"
										checked={allSelected}
										onChange={toggleAll}
										disabled={!holidays.length}
										title={
											allSelected
												? "Deselect all"
												: partiallySelected
													? "Select all"
													: "Select all"
										}
									/>
								</th>
								<th rowSpan={2} className="p-2 text-left">
									Date
								</th>
								<th rowSpan={2} className="p-2 text-left">
									Name
								</th>
								<th rowSpan={2} className="p-2 text-left">
									Type
								</th>
								<th rowSpan={2} className="p-2 text-left">
									Scope
								</th>

								{/* GROUPS SUPER HEADER */}
								<th
									colSpan={effectiveGroupCategoryKeys.length}
									className="p-2 text-center font-semibold whitespace-nowrap"
								>
									Groups
								</th>

								<th rowSpan={2} className="p-2 pl-4 text-left">
									Tags
								</th>
							</tr>

							<tr>
								{effectiveGroupCategoryKeys.map((categoryKey) => (
									<th key={categoryKey} className="p-2 text-left font-medium whitespace-nowrap w-max">
										{capitalizeFirst(getGroupHeaderLabel(categoryKey))}
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{holidays.map((h) => {
								const key = holidayKey(h);

								return (
									<tr key={key} className="border-b">
										<td className="p-2 text-center">
											<input
												type="checkbox"
												checked={!!selected[key]}
												onChange={() => toggleHoliday(key)}
											/>
										</td>
										<td className="p-2">
											{DateTime.fromISO(h.startDate).toLocaleString(DateTime.DATE_MED)}
											{h.endDate &&
												h.endDate !== h.startDate &&
												` – ${DateTime.fromISO(h.endDate).toLocaleString(DateTime.DATE_MED)}`
											}
										</td>
										<td className="p-2">
											{getLocalizedText(h.name, language)}
										</td>
										<td className="p-2">{h.type}</td>

										<td className="p-2">
											{h.temporalScope ?? "—"}
										</td>

										{effectiveGroupCategoryKeys.map((categoryKey) => {
											if (categoryKey === "__GROUPS__") {
												return (
													<td key={categoryKey} className="p-2 whitespace-nowrap w-max">
														—
													</td>
												);
											}

											const names = Array.isArray(h.groups)
												? h.groups
													.map(gr => groupsByCode[gr.code])
													.filter(g => g && g.categoryKey === categoryKey)
													.map(g => g.nameText)
												: [];

											return (
												<td key={categoryKey} className="p-2 whitespace-nowrap w-max">
													{names.length > 0 ? names.join(", ") : "—"}
												</td>
											);
										})}

										<td className="p-2 pl-4">
											{Array.isArray(h.tags) && h.tags.length > 0
												? h.tags.join(", ")
												: "—"}
										</td>
									</tr>
								)
							})}

							{!holidays.length && (
								<tr>
									<td colSpan={6 + effectiveGroupCategoryKeys.length} className="p-4 text-center text-gray-400">
										No holidays loaded
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>

				<div className="flex justify-between items-baseline gap-2">
					<ColorSelectorGroup colors={colors} items={colorOptions} onChange={(values) => {
						setSelectedColors(values);
					}} />
					<div>
						<Button label="Back" onClick={() => cancelAndClose()} variant="secondary" className="mr-5" />
						<Button label="Add Selected" onClick={() => saveAndClose()} />
					</div>
				</div>
			</div>
		</div>
	);
};

export default ImportHolidays;